import fileSaver from "file-saver";
import pickBy from "lodash-es/pickBy";
import sortBy from "lodash-es/sortBy";
import { computed, observable, makeObservable, action } from "mobx";
import { inject, observer } from "mobx-react";
import { fromPromise } from "mobx-utils";
import qs from "query-string";
import { Component } from "react";
import { FormattedMessage } from "react-intl";
import { withRouter, type WithRouterProps } from "nvent-web/utils/withRouter";
import { compose } from "nvent-web/utils/compose";
import * as yup from "yup";

import { SecondaryButton } from "nvent-web/components/Button";
import { LoadingSection } from "nvent-web/components/LoadingSection/LoadingSection";
import Api from "nvent-web/services/Api";
import * as logger from "nvent-web/services/logger";
import type {
  BOMProductSku,
  GetIndependentRecommendationsPayload,
} from "nvent-web/services/resources/ProductResource";
import AppStore from "nvent-web/stores/App";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { ProductsStore } from "nvent-web/stores/Products";
import { UserStore } from "nvent-web/stores/User";
import { type BuildingType } from "nvent-web/types/BuildingType";
import { type DropdownOption } from "nvent-web/types/DropdownOption";
import type {
  CategoryRecommendation,
  CategoryRecommendationVariant,
} from "nvent-web/types/ProductRecommendation";
import {
  type IndependentSelectionGuideRoomDetails,
  selectionGuideRoomDetailsSchema,
  setAwaitingProject,
} from "nvent-web/utils/awaitingProject";
import contactDataForCountries from "nvent-web/utils/contactDataForCountries";
import { createInitialFromPromise } from "nvent-web/utils/createInitialFromPromise";
import { recommendationToProductSpecs } from "nvent-web/utils/recommendationToProductSpecs";

import StepHeading from "../SelectionGuide/components/StepHeading/StepHeading";

import { RecommendationCard } from "./RecommendationCard";
import style from "./RecommendationsStep.module.scss";

interface RecommendationsStepProps {
  createProjectDisabled?: boolean;
}

interface RecommendationsStepInnerProps
  extends RecommendationsStepProps, WithRouterProps {
  api: Api;
  products: ProductsStore;
  notifications: NotificationsStore;
  user: UserStore;
  app: AppStore;
}

interface IndependentSelectionGuideValues extends IndependentSelectionGuideRoomDetails {
  countryCode: string;
  buildingType: BuildingType;
  quickHeating: boolean;
  projectName?: string;
}

const selectionGuideValuesSchema = yup
  .object({
    countryCode: yup.string(),
    buildingType: yup
      .string()
      .oneOf(["new_building", "passive_building", "old_building"])
      .required(),
    projectName: yup.string(),
    quickHeating: yup.boolean().notRequired(),
  })
  .concat(
    selectionGuideRoomDetailsSchema
  ) as yup.ObjectSchema<IndependentSelectionGuideValues>;

const sortThermostats = (thermostatOptions: DropdownOption[]) => {
  const thermostatsNamesOrder = [
    "R-SENZ-WIFI",
    "R-GREEN-LEAF",
    "R-NRG-DM",
    "R-TE",
  ];

  return sortBy(thermostatOptions, (thermostat) => {
    const titleIndex = thermostatsNamesOrder.indexOf(thermostat.title);
    return titleIndex >= 0 ? titleIndex : thermostatsNamesOrder.length;
  });
};

class RecommendationsStepInner extends Component<RecommendationsStepInnerProps> {
  private recommendationsPromise = createInitialFromPromise<
    CategoryRecommendation[]
  >([]);

  private roomDetails: IndependentSelectionGuideValues | null = null;
  private createProjectDisabled = false;
  private user;
  private products;

  constructor(props: RecommendationsStepInnerProps) {
    super(props);

    this.createProjectDisabled = props.createProjectDisabled ?? false;
    this.user = props.user;
    this.products = props.products;

    makeObservable<
      RecommendationsStepInner,
      "recommendationsPromise" | "roomDetails" | "createProjectDisabled"
    >(this, {
      recommendationsPromise: observable,
      roomDetails: observable,
      createProjectDisabled: observable,
      recommendations: computed,
      countryCode: computed,
      customerSupportPhoneNumber: computed,
      thermostatSkuOptions: computed,
      setRoomDetails: action.bound,
      setRecommendationsPromise: action.bound,
    });
  }

  get recommendations(): CategoryRecommendation[] {
    return this.recommendationsPromise.case({
      pending: () => [],
      fulfilled: (value) => value,
      rejected: () => [],
    });
  }

  setRoomDetails(value: IndependentSelectionGuideValues | null) {
    this.roomDetails = value;
  }

  get countryCode() {
    const { user } = this;

    if (user.isReady) {
      return user.companyAddress.countryCode;
    }

    if (this.roomDetails && this.roomDetails.countryCode) {
      return this.roomDetails.countryCode;
    }

    return "";
  }

  get customerSupportPhoneNumber() {
    if (!this.roomDetails) {
      return "";
    }

    const matchedContactData = contactDataForCountries.find(
      ({ countryCode }) => countryCode === this.countryCode.toUpperCase()
    );

    return matchedContactData ? matchedContactData.phoneNumber : "";
  }

  get thermostatSkuOptions() {
    const thermostatSkuOptions = this.products.availableThermostatSkus.map(
      ({ id, description }) => ({
        key: id.toString(),
        title: description,
      })
    );

    return sortThermostats(thermostatSkuOptions);
  }

  setRecommendationsPromise(value: typeof this.recommendationsPromise) {
    this.recommendationsPromise = value;
  }

  componentDidMount() {
    this.setRoomDetails(this.parseSelectionGuideValuesFromUrl());

    const { products } = this.props;

    if (!this.roomDetails) {
      this.props.navigate("/selection-guide/insulation");
      return;
    }

    this.setRecommendationsPromise(
      fromPromise(
        this.fetchRecommendations({
          ...this.roomDetails,
          countryCode: this.countryCode,
        })
      )
    );

    if (this.roomDetails.countryCode) {
      products.loadProductsCatalogForCountry(this.roomDetails.countryCode);
    } else {
      products.loadProductsCatalog();
    }
  }

  componentDidUpdate(prevProps: RecommendationsStepInnerProps) {
    if (prevProps.createProjectDisabled !== this.props.createProjectDisabled) {
      this.createProjectDisabled = this.props.createProjectDisabled ?? false;
    }
  }

  render() {
    return (
      <>
        <StepHeading step={3} />
        {this.renderContent()}
      </>
    );
  }

  private renderContent = () => {
    if (this.recommendationsPromise.state === "pending") {
      return <LoadingSection />;
    }

    if (this.recommendations.length === 0) {
      return (
        <>
          <div className={style.noProducts}>
            <FormattedMessage id="selectionGuide.noProducts" />
            <br />
            {this.customerSupportPhoneNumber && (
              <>
                <FormattedMessage id="selectionguide.noProducts.customerService" />
                <a
                  className={style.phoneNumber}
                  href={`tel:${this.customerSupportPhoneNumber}`}
                >
                  {this.customerSupportPhoneNumber}
                </a>
              </>
            )}
          </div>
          <div className={style.noProductsPhoneNumber} />
          <SecondaryButton onClick={this.goBack} className={style.cancelButton}>
            <FormattedMessage id="actions.cancel" />
          </SecondaryButton>
        </>
      );
    }

    return (
      <>
        {this.recommendations.map(({ recommendations }) =>
          recommendations.map((recommendation) => (
            <RecommendationCard
              key={recommendation.productSkus[0].id}
              onCreateProject={
                !this.createProjectDisabled ? this.createProject : undefined
              }
              onDownloadBom={this.downloadBillOfMaterials}
              thermostatSkuOptions={this.thermostatSkuOptions}
              recommendation={recommendation}
              countryCode={this.roomDetails?.countryCode}
            />
          ))
        )}
        <div>
          <SecondaryButton onClick={this.goBack} className={style.cancelButton}>
            <FormattedMessage id="actions.back" />
          </SecondaryButton>
          <SecondaryButton
            onClick={this.redirectToSelectionGuide}
            className={style.cancelButton}
          >
            <FormattedMessage id="selectionGuide.newSelection" />
          </SecondaryButton>
        </div>
      </>
    );
  };

  private fetchRecommendations = async (
    values: GetIndependentRecommendationsPayload
  ): Promise<CategoryRecommendation[]> => {
    return (await this.props.api.products.getIndependentRecommendations(values))
      .data;
  };

  private goBack = () => {
    if (this.roomDetails) {
      this.props.navigate({
        pathname: "/selection-guide/room",
        search: qs.stringify(this.roomDetails),
      });
    }
  };

  private redirectToSelectionGuide = () => {
    this.props.navigate("/selection-guide");
  };

  private createProject = (recommendation: CategoryRecommendationVariant) => {
    if (!this.roomDetails) {
      return;
    }

    const productSkus = recommendationToProductSpecs(recommendation);
    setAwaitingProject({ roomDetails: this.roomDetails, productSkus });
    this.props.navigate("/projects/continue");
  };

  private parseSelectionGuideValuesFromUrl = () => {
    try {
      const urlParams = qs.parse(window.location.search);
      const values = selectionGuideValuesSchema.validateSync(urlParams);
      return values;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      return null;
    }
  };

  private downloadBillOfMaterials = async ({
    products,
    comments,
  }: {
    products: CategoryRecommendationVariant;
    comments: string;
  }) => {
    if (!this.roomDetails) {
      return;
    }

    const productSkus = recommendationToProductSpecs(products).map(
      (recommendation) => pickBy(recommendation)
    ) as BOMProductSku[];

    try {
      const {
        area,
        areaType,
        floorConstruction,
        floorFinish,
        installableArea,
        projectName = "",
      } = this.roomDetails;
      const { user, api } = this.props;

      const payload = {
        countryCode: this.countryCode,
        productSkus,
        area,
        roomType: areaType,
        floorConstruction,
        floorFinish,
        installableArea,
        locale: this.props.app.locale,
        comments,
        projectName,
      };

      const { data } = await (user.isReady
        ? api.products.downloadProductOnlyBOMv2(payload)
        : api.products.downloadPublicProductOnlyBOMv2(payload));

      fileSaver.saveAs(data, "Bill_of_materials.pdf");
    } catch (error) {
      logger.error(error);
      this.props.notifications.createError(
        <FormattedMessage id="error.reportDownloadFailed" />
      );
    }
  };
}

export const RecommendationsStep = compose<RecommendationsStepProps>(
  withRouter,
  inject("api", "notifications", "products", "user", "app"),
  observer
)(RecommendationsStepInner);
